import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_CONFIG, getAuthUrl, getBlobticUrl } from '../config/api.config';
import { blobticMessage } from '../components/BlobticMessage';
import { blobticLoading } from '../components/BlobticLoadingOverlay';
import { globalAuthService } from '../contexts/AuthContext';
import { BlobticDataManager } from './blobticDataManager';

// Interface pour la rÃ©ponse standard de l'API Zoom
export interface ZoomResponse<T> {
    success: boolean;
    data: T;
    message?: string;
    code?: string;
    // boubaker 22/08/2026 - IndexDB : mÃ©tadonnÃ©es serveur (idexDbKey / idexDbClosed)
    metadata?: {
        idexDbKey?: { suidbIdf?: number; objStore?: string; idxDbName?: string; type?: string };
        idexDbClosed?: { suidbIdf?: number; objStore?: string; type?: string }[];
        [key: string]: any;
    } | null;
}

/**
 * boubaker 22/08/2026 - IndexDB multi-utilisateurs.
 * Applique les stores invalidÃ©s renvoyÃ©s par le serveur (lignes fermÃ©es
 * TABP_SYS_USER_IDX_DB) : vide les object stores concernÃ©s pour que les donnÃ©es
 * soient rechargÃ©es depuis le serveur au prochain accÃ¨s.
 */
const applyIdexDbClosed = async (metadata?: ZoomResponse<any>['metadata']): Promise<void> => {
    try {
        const closed = metadata?.idexDbClosed;
        if (Array.isArray(closed) && closed.length > 0) {
            const storeNames = closed
                .map((c) => c?.objStore || '')
                .filter((n) => typeof n === 'string' && n.length > 0);
            if (storeNames.length > 0) {
                await BlobticDataManager.removeStores(storeNames);
            }
        }
    } catch { /* silencieux */ }
};

// boubaker 22/08/2026 - objets volatils jamais mis en cache IndexDB
// (traÃ§age temps rÃ©el : trafic API, actions et connexions utilisateurs ;
// planning Academy : sÃ©ances modifiÃ©es en permanence par le Planning Matrix)
const NON_CACHEABLE_OBJECTS = ['traffic', 'user_action', 'academy_session'];

// Interface pour les requÃªtes Zoom
export interface ZoomRequest {
    ZoomKey: string;
    Action: {
        OKey: string;
        [key: string]: string | number | boolean | null | undefined; // all primitive types
    };
    Grill?: {
        Champs: string;
        Labels: string;
    };
    useCache?: boolean;
    UseCache?: boolean;
    showLoader?: boolean;
    ShowLoader?: boolean;
}

class BlobticService {
    /**
     * ExÃ©cute une requÃªte gÃ©nÃ©rique post vers l'endpoint Zoom avec gestion du stock IndexedDB
     * @param request La requÃªte Zoom Ã  envoyer
     * @param useCache Indique si le cache IndexedDB doit être vérifié pour les requêtes Select (défaut: true)
     * @param showLoader Affiche l'overlay de chargement pendant l'appel serveur (défaut: true)
     * @returns La réponse typée
     */
    async executeZoomRequest<T>(
        request: ZoomRequest,
        useCache: boolean = true,
        showLoader: boolean = true
    ): Promise<ZoomResponse<T>> {
        const token = sessionStorage.getItem('blobtic_token');

        if (!token) {
            throw new Error("Vous n'Ãªtes pas connectÃ©");
        }

        const effectiveCache = request.useCache ?? request.UseCache ?? useCache;
        const effectiveShowLoader = request.showLoader ?? request.ShowLoader ?? showLoader;
        const okey = request?.Action?.OKey || '';
        let objectName = '';
        let action = '';

        if (okey.includes(',')) {
            const parts = okey.split(',');
            if (parts.length >= 3) {
                objectName = parts[1].trim();
                action = parts[2].trim().toLowerCase();
            } else if (parts.length >= 2) {
                objectName = parts[1].trim();
                action = parts[1].trim().toLowerCase();
            }
        }

        // Handle Select Caching via BlobticDataManager (IndexedDB)
        if (effectiveCache && action === 'select' && objectName && BlobticDataManager.isValidContext() && !NON_CACHEABLE_OBJECTS.includes(objectName.toLowerCase())) {
            const cachedData = await BlobticDataManager.getData<any>(objectName);
            if (cachedData && cachedData.length > 0) {
                return {
                    success: true,
                    data: cachedData as unknown as T
                };
            }
        }

        const config: AxiosRequestConfig = {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        };

        if (effectiveShowLoader) blobticLoading.show();
        try {
            const response: AxiosResponse<ZoomResponse<T>> = await axios.post(
                getBlobticUrl('ZOOM'),
                request,
                config
            );

            // boubaker 23/08/2026 - HTTP 204 No Content : suppression réussie sans corps.
            // On normalise en { success: true } et on invalide le cache IndexDB de l'objet.
            if (response.status === 204 || response.data === null || response.data === undefined || response.data === ('' as any)) {
                if (objectName && action === 'delete' && BlobticDataManager.isValidContext()) {
                    void BlobticDataManager.removeItem(objectName, request.Action)
                        .catch(() => { /* silencieux */ });
                }
                return {
                    success: true,
                    data: [] as unknown as T
                };
            }

            // Handle logical errors (200 OK but success: false)
            if (response.data && response.data.success === false) {
                const errorMsg = response.data.message || (response.data as any).error || 'Unknown Error';
                const code = response.data.code || (response.data as any).code;

                if (code === 'SESSION_LOST' || code === 'UNAUTHORIZED' || code === 'TOKEN_EXPIRED') {
                    if (globalAuthService.handleSessionExpired) {
                        globalAuthService.handleSessionExpired();
                    } else if (globalAuthService.logout) {
                        globalAuthService.logout();
                    }
                } else if (code === 'ACCESS_DENIED') {
                    blobticMessage.error('Error', 'Access Denied');
                } else {
                    blobticMessage.error('Error', errorMsg);
                }
            } else if (response.data && response.data.success) {
                // Synchronize result with IndexedDB BlobticDataManager
                // (sauf si contexte invalide / désactivé ou objets volatils)
                const isCacheable = objectName && BlobticDataManager.isValidContext() && !NON_CACHEABLE_OBJECTS.includes(objectName.toLowerCase());
                if (objectName && isCacheable) {
                    if (action === 'select' && Array.isArray(response.data.data)) {
                        // boubaker 22/08/2026 - Stockage BRUT (noms de colonnes d'origine, sans
                        // mapping) et NON BLOQUANT : le cache mémoire est rempli de façon
                        // synchrone dès l'appel, la persistance IndexedDB se fait en arrière-plan.
                        // La réponse est rendue au composant immédiatement après réception serveur.
                        void BlobticDataManager.setData(objectName, response.data.data)
                            .catch(() => { /* silencieux */ });
                    } else if (action === 'delete') {
                        void BlobticDataManager.removeItem(objectName, request.Action)
                            .catch(() => { /* silencieux */ });
                    } else {
                        // Any mutation action (insert, update, approve, reject, etc.)
                        const item = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data;
                        if (item && typeof item === 'object') {
                            void BlobticDataManager.upsertItem(objectName, item)
                                .catch(() => { /* silencieux */ });
                        }
                    }
                }

                // boubaker 22/08/2026 - IndexDB : vider les stores invalidés APRÈS la synchro locale.
                if (BlobticDataManager.isValidContext()) {
                    await applyIdexDbClosed(response.data.metadata);
                }
            }

            return response.data;
        } catch (error: any) {
            if (axios.isAxiosError(error) && error.response) {
                const status = error.response.status;
                const errorData = error.response.data;
                const code = errorData?.code;
                const errorMsg = errorData?.error || errorData?.message || `Erreur ${status}`;

                if (status === 401 || status === 403 || code === 'SESSION_LOST') {
                    if (globalAuthService.handleSessionExpired) {
                        globalAuthService.handleSessionExpired();
                    } else if (globalAuthService.logout) {
                        globalAuthService.logout();
                    }
                }

                return {
                    success: false,
                    data: null as any,
                    message: errorMsg,
                    code: code || (status === 401 ? 'SESSION_LOST' : `HTTP_${status}`)
                };
            }
            throw error;
        } finally {
            if (effectiveShowLoader) blobticLoading.hide();
        }
    }
}

export const blobticService = new BlobticService();
export default blobticService;
